import React, { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

// Categories definition
const categories = [
  { id: 'assistants', gradient: ['#3dd3ee', '#61525a'], label: 'Assistants & Chatbots' },
  { id: 'documents', gradient: ['#fad24b', '#ff8c19'], label: 'Traitement de documents' },
  { id: 'generation', gradient: ['#b4dc19', '#3dd3ee'], label: 'Génération de contenu' },
  { id: 'vision', gradient: ['#c8aff0', '#3dd3ee'], label: 'Vision & OCR' },
  { id: 'ia-custom', gradient: ['#ff8c19', '#fad24b'], label: 'IA sur-mesure' },
  { id: 'data', gradient: ['#61525a', '#b4dc19'], label: 'Data & Intégration' },
];

// Project tags with category assignment
const allTags = [
  // Assistants & Chatbots
  { label: 'Chatbot interne pour équipes RH', gradient: ['#3dd3ee', '#61525a'], category: 'assistants' },
  { label: 'Recherche dans 10 000+ documents', gradient: ['#61525a', '#3dd3ee'], category: 'assistants' },
  { label: 'Assistant commercial augmenté', gradient: ['#3dd3ee', '#b4dc19'], category: 'assistants' },
  { label: 'Réponses automatiques aux emails clients', gradient: ['#3dd3ee', '#283750'], category: 'assistants' },
  { label: 'Base de connaissances intelligente', gradient: ['#3dd3ee', '#61525a'], category: 'assistants' },
  { label: 'FAQ dynamique auto-alimentée', gradient: ['#61525a', '#3dd3ee'], category: 'assistants' },
  
  // Traitement de documents
  { label: 'Extraction automatique de factures', gradient: ['#fad24b', '#ff8c19'], category: 'documents' },
  { label: 'Lecture de contrats PDF', gradient: ['#fad24b', '#61525a'], category: 'documents' },
  { label: 'Analyse de rapports financiers', gradient: ['#ff8c19', '#fad24b'], category: 'documents' },
  { label: 'Traitement de formulaires scannés', gradient: ['#fad24b', '#ff8c19'], category: 'documents' },
  { label: 'Extraction de données de bons de commande', gradient: ['#fad24b', '#3dd3ee'], category: 'documents' },
  { label: 'Numérisation intelligente d\'archives', gradient: ['#ff8c19', '#fad24b'], category: 'documents' },
  
  // Génération de contenu
  { label: 'Génération de fiches produits e-commerce', gradient: ['#b4dc19', '#3dd3ee'], category: 'generation' },
  { label: 'Rédaction automatique de descriptions', gradient: ['#b4dc19', '#fad24b'], category: 'generation' },
  { label: 'Création de rapports sectoriels', gradient: ['#3dd3ee', '#b4dc19'], category: 'generation' },
  { label: 'Synthèse de comptes-rendus de réunion', gradient: ['#b4dc19', '#c8aff0'], category: 'generation' },
  { label: 'Génération de contenus marketing', gradient: ['#b4dc19', '#ff8c19'], category: 'generation' },
  { label: 'Résumés automatiques de documents longs', gradient: ['#b4dc19', '#61525a'], category: 'generation' },
  
  // Vision & OCR
  { label: 'Reconnaissance d\'ordonnances médicales', gradient: ['#c8aff0', '#3dd3ee'], category: 'vision' },
  { label: 'Lecture de cartes de mutuelle', gradient: ['#c8aff0', '#61525a'], category: 'vision' },
  { label: 'Détection de défauts sur chaîne de production', gradient: ['#c8aff0', '#ff8c19'], category: 'vision' },
  { label: 'Classification de photos produits', gradient: ['#c8aff0', '#fad24b'], category: 'vision' },
  { label: 'Analyse de documents d\'identité', gradient: ['#c8aff0', '#b4dc19'], category: 'vision' },
  { label: 'Extraction de données depuis captures écran', gradient: ['#61525a', '#c8aff0'], category: 'vision' },
  
  // IA sur-mesure
  { label: 'Modèle IA entraîné sur vos données métier', gradient: ['#ff8c19', '#fad24b'], category: 'ia-custom' },
  { label: 'Catégorisation automatique de tickets support', gradient: ['#ff8c19', '#3dd3ee'], category: 'ia-custom' },
  { label: 'Prédiction de satisfaction client', gradient: ['#ff8c19', '#b4dc19'], category: 'ia-custom' },
  { label: 'Scoring automatique de leads', gradient: ['#ff8c19', '#c8aff0'], category: 'ia-custom' },
  { label: 'Détection d\'anomalies dans les données', gradient: ['#ff8c19', '#61525a'], category: 'ia-custom' },
  { label: 'Recommandation de produits personnalisée', gradient: ['#fad24b', '#ff8c19'], category: 'ia-custom' },
  
  // Data & Intégration
  { label: 'Scraping de données concurrents', gradient: ['#61525a', '#b4dc19'], category: 'data' },
  { label: 'Veille sectorielle automatisée', gradient: ['#61525a', '#3dd3ee'], category: 'data' },
  { label: 'Enrichissement de base CRM', gradient: ['#3dd3ee', '#61525a'], category: 'data' },
  { label: 'Nettoyage et structuration de données', gradient: ['#61525a', '#ff8c19'], category: 'data' },
  { label: 'Intégration IA dans votre ERP/CRM', gradient: ['#61525a', '#3dd3ee'], category: 'data' },
  { label: 'API IA sur-mesure pour vos outils', gradient: ['#61525a', '#c8aff0'], category: 'data' },
];

// Distribute tags into rails
const createRails = (tags) => {
  const railCount = 6;
  const rails = Array.from({ length: railCount }, (_, i) => ({
    speed: 30 + (i * 5),
    direction: i % 2 === 0 ? 1 : -1,
    tags: [],
  }));
  
  tags.forEach((tag, index) => {
    rails[index % railCount].tags.push(tag);
  });
  
  return rails.filter(rail => rail.tags.length > 0);
};

const GlassTag = ({ label, gradient, isHovered, onHover, onLeave, isVisible }) => {
  const [color1, color2] = gradient;
  
  return (
    <div
      className={`
        relative px-6 py-3 rounded-2xl cursor-pointer
        backdrop-blur-xl border border-white/20
        transition-all duration-500 ease-out
        flex-shrink-0
        ${isHovered ? 'scale-110 z-20' : 'hover:scale-105'}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
      `}
      style={{
        background: `linear-gradient(135deg, ${color1}40, ${color2}30)`,
        boxShadow: isHovered
          ? `0 8px 32px ${color1}40, 0 0 60px ${color1}20, inset 0 1px 1px rgba(255,255,255,0.3)`
          : `0 4px 16px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.2)`,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Glass shine effect */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-50 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
        }}
      />
      
      {/* Inner glow */}
      <div 
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 30% 0%, ${color1}20 0%, transparent 50%)`,
        }}
      />
      
      <span 
        className="relative text-sm font-medium whitespace-nowrap"
        style={{
          color: 'rgba(255,255,255,0.95)',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
        }}
      >
        {label}
      </span>
    </div>
  );
};

const Rail = ({ tags, speed, direction, activeCategories }) => {
  const [hoveredTag, setHoveredTag] = useState(null);
  
  // Filter tags based on active categories
  const visibleTags = tags.filter(tag => activeCategories.includes(tag.category));
  
  if (visibleTags.length === 0) return null;
  
  // Double the tags for seamless loop
  const duplicatedTags = [...visibleTags, ...visibleTags];
  
  return (
    <div className="relative overflow-hidden py-2">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#1e1919] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#1e1919] to-transparent z-10 pointer-events-none" />
      
      <div
        className="flex gap-4 transition-opacity duration-500"
        style={{
          animation: `scroll-${direction === 1 ? 'right' : 'left'} ${speed}s linear infinite`,
          animationPlayState: hoveredTag !== null ? 'paused' : 'running',
        }}
      >
        {duplicatedTags.map((tag, index) => (
          <GlassTag
            key={`${tag.label}-${index}`}
            label={tag.label}
            gradient={tag.gradient}
            isHovered={hoveredTag === index}
            onHover={() => setHoveredTag(index)}
            onLeave={() => setHoveredTag(null)}
            isVisible={true}
          />
        ))}
      </div>
    </div>
  );
};

const CategoryFilter = ({ category, isActive, onClick }) => {
  const [color1, color2] = category.gradient;
  
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-full
        border transition-all duration-300
        ${isActive 
          ? 'border-white/30 bg-white/10' 
          : 'border-white/10 bg-transparent opacity-40 hover:opacity-70'
        }
      `}
    >
      <div
        className={`w-4 h-4 rounded-full border transition-all duration-300 ${
          isActive ? 'border-white/40 scale-100' : 'border-white/20 scale-75'
        }`}
        style={{ 
          background: `linear-gradient(135deg, ${color1}${isActive ? '80' : '40'}, ${color2}${isActive ? '60' : '30'})`,
        }}
      />
      <span className={`text-sm transition-colors duration-300 ${
        isActive ? 'text-white' : 'text-[#bbb5ae]'
      }`}>
        {category.label}
      </span>
    </button>
  );
};

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategories, setActiveCategories] = useState(categories.map(c => c.id));
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const toggleCategory = (categoryId) => {
    setActiveCategories(prev => {
      if (prev.includes(categoryId)) {
        // Don't allow deselecting all categories
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const selectAllCategories = () => {
    setActiveCategories(categories.map(c => c.id));
  };

  // Create rails from filtered tags
  const filteredTags = allTags.filter(tag => activeCategories.includes(tag.category));
  const rails = createRails(allTags); // We pass all tags, filtering happens in Rail component

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="relative py-24 bg-[#1e1919] overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-[#3dd3ee]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#fad24b]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#c8aff0]/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.008)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.008)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative">
        {/* Section Header */}
        <div
          className={`text-center mb-12 px-6 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-sm font-medium text-[#bbb5ae] mb-6">
            <Sparkles className="w-4 h-4 text-[#fad24b]" />
            +50 projets livrés en production
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Trouvez votre besoin dans nos{' '}
            <span className="text-[#3dd3ee]">réalisations</span>
          </h2>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto">
            Des projets concrets déployés chez nos clients — filtrez par catégorie
          </p>
        </div>

        {/* Category Filters */}
        <div
          className={`flex flex-wrap justify-center gap-3 mb-8 px-6 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {categories.map((category) => (
            <CategoryFilter
              key={category.id}
              category={category}
              isActive={activeCategories.includes(category.id)}
              onClick={() => toggleCategory(category.id)}
            />
          ))}
          {activeCategories.length < categories.length && (
            <button
              onClick={selectAllCategories}
              className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#3dd3ee] hover:bg-white/5 transition-all duration-300"
            >
              Tout afficher
            </button>
          )}
        </div>

        {/* Rails Container */}
        <div
          className={`space-y-3 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {rails.map((rail, index) => (
            <Rail
              key={index}
              tags={rail.tags}
              speed={rail.speed}
              direction={rail.direction}
              activeCategories={activeCategories}
            />
          ))}
        </div>

        {/* Active count */}
        <div
          className={`mt-8 text-center transition-all duration-500 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="text-sm text-[#736c64]">
            {filteredTags.length} projet{filteredTags.length > 1 ? 's' : ''} affiché{filteredTags.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-8 text-center px-6 transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="text-[#bbb5ae]">
            Vous avez un besoin similaire ?{' '}
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-[#3dd3ee] hover:text-[#fad24b] transition-colors font-medium underline underline-offset-4"
            >
              Parlons-en
            </button>
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes scroll-right {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0%);
          }
        }
        
        @keyframes scroll-left {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;
